// src/components/ui/Skeleton.jsx
// Componentes de skeleton loading para una experiencia de carga fluida

import React from 'react';

/**
 * Skeleton - Componente base de skeleton loading
 */
export function Skeleton({ className = '', width, height, rounded = 'md' }) {
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full',
  };

  return (
    <div 
      className={`skeleton ${roundedClasses[rounded]} ${className}`}
      style={{ 
        width: width || '100%', 
        height: height || '20px',
        minHeight: height || '20px'
      }}
    />
  );
}

/**
 * SkeletonText - Skeleton para texto con múltiples líneas
 */
export function SkeletonText({ lines = 3, className = '', lastLineWidth = '60%' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          height="14px" 
          width={i === lines - 1 ? lastLineWidth : '100%'}
          rounded="sm"
        />
      ))}
    </div>
  );
}

/**
 * SkeletonCard - Skeleton para tarjetas
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <Skeleton width="48px" height="48px" rounded="xl" />
        <div className="flex-1 space-y-3">
          <Skeleton height="20px" width="60%" rounded="md" />
          <Skeleton height="14px" width="80%" rounded="sm" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton height="12px" width="100%" rounded="sm" />
        <Skeleton height="12px" width="90%" rounded="sm" />
        <Skeleton height="12px" width="70%" rounded="sm" />
      </div>
    </div>
  );
}

/**
 * SkeletonTable - Skeleton para tablas
 */
export function SkeletonTable({ rows = 5, columns = 4, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      <div className="flex gap-4 pb-3 border-b border-white/10">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height="16px" className="flex-1" rounded="sm" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 py-2">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton 
              key={colIndex} 
              height="14px" 
              className="flex-1" 
              rounded="sm"
              width={colIndex === 0 ? '70%' : '100%'}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * SkeletonAvatar - Skeleton para avatares
 */
export function SkeletonAvatar({ size = 'md', className = '' }) {
  const sizes = {
    sm: '32px',
    md: '48px',
    lg: '64px',
    xl: '96px',
  };

  return (
    <Skeleton 
      width={sizes[size]} 
      height={sizes[size]} 
      rounded="full" 
      className={className}
    />
  );
}

/**
 * SkeletonButton - Skeleton para botones
 */
export function SkeletonButton({ size = 'md', className = '' }) {
  const sizes = {
    sm: { width: '80px', height: '32px' },
    md: { width: '120px', height: '40px' },
    lg: { width: '160px', height: '48px' },
  };

  return (
    <Skeleton 
      width={sizes[size].width} 
      height={sizes[size].height} 
      rounded="lg" 
      className={className}
    />
  );
}

/**
 * SkeletonForm - Skeleton para formularios
 */
export function SkeletonForm({ fields = 4, className = '' }) {
  return (
    <div className={`space-y-6 ${className}`}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton height="14px" width="30%" rounded="sm" />
          <Skeleton height="44px" width="100%" rounded="lg" />
        </div>
      ))}
      <div className="flex gap-3 pt-4">
        <SkeletonButton size="md" />
        <SkeletonButton size="md" />
      </div>
    </div>
  );
}

/**
 * SkeletonGrid - Skeleton para grids de cards
 */
export function SkeletonGrid({ items = 6, columns = 3, className = '' }) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-6 ${className}`}>
      {Array.from({ length: items }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * SkeletonChat - Skeleton para mensajes de chat
 */
export function SkeletonChat({ messages = 3, className = '' }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: messages }).map((_, i) => (
        <div 
          key={i} 
          className={`flex gap-3 ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}
        >
          <SkeletonAvatar size="sm" />
          <div className={`space-y-2 max-w-[70%] ${i % 2 === 0 ? '' : 'items-end'}`}>
            <Skeleton 
              height="14px" 
              width="80px" 
              rounded="sm" 
            />
            <div className="bg-white/5 rounded-2xl p-4 space-y-2">
              <Skeleton height="12px" width="200px" rounded="sm" />
              <Skeleton height="12px" width="160px" rounded="sm" />
              {i % 2 === 0 && <Skeleton height="12px" width="120px" rounded="sm" />}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * SkeletonDashboard - Skeleton completo para dashboard
 */
export function SkeletonDashboard({ className = '' }) {
  return (
    <div className={`space-y-8 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton height="32px" width="200px" rounded="md" />
          <Skeleton height="16px" width="300px" rounded="sm" />
        </div>
        <SkeletonButton size="md" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white/5 rounded-xl p-4 space-y-3">
            <Skeleton height="14px" width="60%" rounded="sm" />
            <Skeleton height="28px" width="40%" rounded="md" />
            <Skeleton height="12px" width="80%" rounded="sm" />
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SkeletonCard />
        </div>
        <div>
          <SkeletonCard />
        </div>
      </div>
    </div>
  );
}

export default Skeleton;
